<!-- ShelfWall — the photographic café wall, drawn in the DOM.

     A strip of bay photos ("plates", public/wall/bay-1..4.webp) that share one
     camera, and the real bags stood on their boards by src/lib/placement.js.
     Each bag is a real <button>: the stock stacked behind it, a contact
     shadow, a drop shadow toward the plates' light, the board's light grade
     and a laminate sheen (BagFront + wall-look.js LOOK).

     Props
       bays         store bays (the current grouping), in order
       matches      Set of slugs the filters light; the rest dim (no layout change)
       picked       slug of the picked coffee: it lifts off the board
       reducedMotion
       interactive  false = a static, non-tappable picture (e.g. a Roaster page)
       active       false = the page is not the one on screen (a Coffee or
                    Roaster page pushed over the Shelf): nothing on the wall
                    is a control until it comes back
       initialBay   the bay to open on (a ?bay= deep link)
       cardBottom   px from the scroller's top to the bottom of whatever floats over
       dockTop      the top of it and the top of the dock below: the wall is scaled
                    and offset so the shelves fill that band (null = fill the scroller)
     Emits
       pick { slug, rect }   bayInView key   ready   drag (first user pan)   release (Esc)
     Exposes
       focusBay(key, { instant })   walk(dir)   bayItems(key)   itemOf(slug)   currentBayKey -->
<template>
  <div
    ref="host"
    class="wall-scroller"
    :class="{ 'is-dragging': dragging, 'is-static': !interactive }"
    :style="{ scrollSnapType: snapType }"
    @scroll.passive="onScroll"
    @pointerdown="onDown"
    @click.capture="onClickCapture"
    @keydown="onKey"
  >
    <div class="wall" :class="{ 'wall--fade': fading }" :style="{ width: `${wallW}px`, height: `${PH}px`, transform: offsetY ? `translateY(${offsetY}px)` : undefined }">
      <div
        v-for="(p, i) in layout.plates"
        :key="`plate-${i}`"
        class="plate"
        :style="plateStyle(p, i)"
        aria-hidden="true"
      />
      <!-- A div, not a <button>, so the element (and the BagFront images in
           it) is never torn down when a bay scrolls in or out of view. Only
           the bags of a bay that is actually in view are controls (role,
           tabindex, name); the rest of the wall is picture: no role, no tab
           stop, hidden from assistive tech, and a tap on it does nothing. -->
      <div
        v-for="it in layout.items"
        :key="it.id"
        :ref="(el) => setSlot(it.id, el)"
        class="slot"
        :class="{ 'slot--dim': isDim(it), 'slot--lifted': isLifted(it), 'slot--off': !isLive(it) }"
        :style="slotStyle(it)"
        :role="isLive(it) ? 'button' : undefined"
        :tabindex="isLive(it) ? (tabbable(it) ? 0 : -1) : undefined"
        :aria-label="isLive(it) && !it.twin ? label(it) : undefined"
        :aria-hidden="!isLive(it) || it.twin ? 'true' : undefined"
        :aria-disabled="isLive(it) && isDim(it) ? 'true' : undefined"
        :data-slug="it.slug"
        :data-bay="it.bay"
        @click="onPick(it, $event)"
      >
        <span class="bag" :style="bagStyle(it)">
          <BagFront
            v-for="k in stackOf(it)"
            :key="`s${k}`"
            class="layer"
            variant="wall"
            :coffee="coffee(it)"
            :size="it.grams"
            :load="isLoaded(it)"
            :style="stackStyle(it, k)"
          />
          <span class="contact" :style="{ background: LOOK.contact }" />
          <BagFront
            class="layer layer--front"
            variant="wall"
            :coffee="coffee(it)"
            :size="it.grams"
            :load="isLoaded(it)"
            :style="frontStyle(it)"
            @ready="onFrontReady(it)"
          />
        </span>
        <span class="clip" :class="{ 'clip--out': !coffee(it)?.inStock }" :style="clipStyle(it)" aria-hidden="true">
          <span class="clip__brand">{{ brand(it) }}</span>
          <span class="clip__name">{{ variety(it) }}</span>
          <span class="clip__price">{{ clip(it) }}</span>
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, shallowRef, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import BagFront from './BagFront.vue';
import { layoutWall, BOARDS } from '../lib/placement.js';
import { LOOK, FRAME, wallInput, clipText, brandText, shortName, hash01 } from '../lib/wall-look.js';
import { coffeeBySlug } from '../store.js';

const props = defineProps({
  bays: { type: Array, required: true },
  matches: { type: Object, default: null },
  picked: { type: String, default: null },
  reducedMotion: { type: Boolean, default: false },
  interactive: { type: Boolean, default: true },
  active: { type: Boolean, default: true },
  initialBay: { type: String, default: '' },
  cardBottom: { type: Number, default: null },
  dockTop: { type: Number, default: null },
});
const emit = defineEmits(['pick', 'bayInView', 'ready', 'drag', 'release']);

const PLATE_ASPECT = 768 / 1376;
const BASE = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');
const PLATE_SRC = [1, 2, 3, 4].map((n) => `${BASE}/wall/bay-${n}.webp`);

const host = ref(null);
const vw = ref(0);
const vh = ref(0);
// --- framing ---------------------------------------------------------------------
//
// The plate height PH and its vertical offset. With a band to fill (cardBottom →
// dockTop), the tallest bag on the top board clears the card by FRAME.cardGap and
// the bottom board's lip sits FRAME.dockGap above the dock (its price clip hangs
// just under the lip and must stay in sight). The photo always covers the whole
// scroller: if the band would leave wall uncovered at the top or bottom, the plate
// grows until it doesn't. A small shortfall at the very top (behind the status
// bar, above the card) is not worth pushing the shelves up into the card for:
// up to FRAME.topFill px there is the plate's own top edge, mirrored (foliage and
// the top pipe), and only beyond that does the plate grow.
const frame = computed(() => {
  const H = vh.value;
  const cb = props.cardBottom; const dt = props.dockTop;
  if (!H || cb == null || dt == null || dt - cb < 80) return { PH: H, offsetY: 0 };
  const reach = BOARDS[0].back - FRAME.topReach;      // tallest bag top on the top board
  const lip = BOARDS[2].front;
  const L = dt - FRAME.dockGap;                       // where the bottom lip goes
  let ph = (L - (cb + FRAME.cardGap)) / (lip - reach);
  let y = cb + FRAME.cardGap - reach * ph;
  if (y > FRAME.topFill) { ph = (L - FRAME.topFill) / lip; y = FRAME.topFill; }   // the photo must reach the top
  if (y + ph < H) { ph = Math.max(ph, (H - L) / (1 - lip)); y = L - lip * ph; }  // …and the scroller's foot
  return { PH: Math.round(ph * 10) / 10, offsetY: Math.round(y * 10) / 10 };
});
const PH = computed(() => frame.value.PH);
const offsetY = computed(() => frame.value.offsetY);
const PW = computed(() => PH.value * PLATE_ASPECT);
const scrollX = ref(0);
const dragging = ref(false);
const fading = ref(false);

// --- layout ------------------------------------------------------------------

const signature = computed(() => props.bays.map((b) => `${b.key}:${b.coffees.map((c) => c.slug).join(',')}`).join('|'));
const layout = shallowRef(layoutWall(wallInput(props.bays)));
const wallW = computed(() => layout.value.plates.length * PW.value);
const bySlug = computed(() => {
  const m = new Map();
  for (const it of layout.value.items) if (!it.twin) m.set(it.slug, it);
  return m;
});

const coffee = (it) => coffeeBySlug(it.slug);
const clip = (it) => { const c = coffee(it); return c ? clipText(c, it.grams) : ''; };
const brand = (it) => brandText(coffee(it));
// Shelf tickets are hand-lettered, not shouted: "COLOMBIA De Cana" → "Colombia De Cana".
const variety = (it) => { const c = coffee(it); return c ? shortName(c, 44).replace(/\b([A-Z])([A-Z]{3,})\b/g, (m, a, b) => a + b.toLowerCase()) : ''; };
function label (it) {
  const c = coffee(it);
  if (!c) return it.slug;
  const price = c.inStock ? (c.fromPrice != null ? `from $${c.fromPrice}` : '') : 'sold out';
  return [shortName(c, 60), c.roaster, price].filter(Boolean).join(', ');
}

const isDim = (it) => !!props.matches && !props.matches.has(it.slug);
const isLifted = (it) => props.picked === it.slug && !it.twin;
const stackOf = (it) => Array.from({ length: Math.max(0, it.facings - 1) }, (_, i) => it.facings - 1 - i); // back to front

// Pixel geometry of an item's slot (the unrotated box the bag stands in).
function box (it) {
  const w = it.w * PW.value; const h = it.h * PH.value;
  return { left: (it.plate + it.x) * PW.value - w / 2, top: it.base * PH.value - h, w, h };
}

function plateStyle (p, i) {
  const bayStart = layout.value.bays.some((b) => b.from === i);
  const phone = PW.value >= vw.value * 0.8;
  return {
    left: `${i * PW.value}px`,
    width: `${PW.value + 0.5}px`,
    backgroundImage: `url(${PLATE_SRC[p]})`,
    '--fill': offsetY.value > 0 ? `${Math.ceil(offsetY.value) + 1}px` : '0px',
    '--ph': `${PH.value}px`,
    scrollSnapAlign: phone ? 'center' : (bayStart ? 'start' : 'none'),
  };
}
function slotStyle (it) {
  const b = box(it);
  return { left: `${b.left}px`, top: `${b.top}px`, width: `${b.w}px`, height: `${b.h}px` };
}
function bagStyle (it) {
  const lifted = isLifted(it);
  const t = [
    lifted ? `translateY(${LOOK.lift.y}px) scale(${LOOK.lift.scale})` : '',
    `perspective(${LOOK.perspective}px) rotateY(${it.ry}deg) rotateZ(${it.rz + it.lean}deg)`,
  ].join(' ');
  return { transform: t, filter: isDim(it) ? LOOK.dim : 'none', transitionDuration: `${LOOK.lift.ms}ms, 320ms` };
}
// Stock behind the front bag: further back on the board is a little higher in the
// picture (the camera looks down on the lower boards), a touch smaller, and in the
// shadow of the bag in front. Only a sliver of seal and side should show.
//
// Each rear bag is placed by hand, not stamped: its own seeded rise, sideways
// nudge, twist, size and shade, and now and then (LOOK.stackJitter.flip) one sits
// out to the other side of the stack. Bag k is placed on top of bag k-1's
// position, so the variation accumulates the way a real stack's does.
function stackStyle (it, k) {
  const J = LOOK.stackJitter;
  const depth = (BOARDS[it.board].front - BOARDS[it.board].back) * PH.value;
  // Capped: on the deep bottom board an uncapped rise sets the stock visibly above the front bag.
  const step = Math.min(depth * LOOK.stackRise[0] + LOOK.stackRise[1], LOOK.stackRise[2]);
  let dx = 0; let dy = 0; let scale = 1; let shade = LOOK.stackBrightness[0];
  for (let j = 1; j <= k; j++) {
    const r = (tag) => hash01(`${it.id}:stack${j}:${tag}`);
    const side = r('flip') < J.flip ? -it.depthSide : it.depthSide;
    dy -= step * (J.rise[0] + (J.rise[1] - J.rise[0]) * r('rise'));
    dx += side * (LOOK.stackDrift[0] + LOOK.stackDrift[1] * j) + (r('dx') - 0.5) * 2 * J.dx;
    scale *= J.scale[0] + (J.scale[1] - J.scale[0]) * r('scale');
    shade -= LOOK.stackBrightness[1] * (J.shade[0] + (J.shade[1] - J.shade[0]) * r('shade'));
  }
  const rz = (hash01(`${it.id}:stack${k}:rz`) - 0.5) * 2 * J.rz;
  return {
    transform: `translate(${dx.toFixed(2)}px, ${dy.toFixed(2)}px) rotate(${rz.toFixed(2)}deg) scale(${scale.toFixed(4)})`,
    filter: `brightness(${shade.toFixed(3)}) ${LOOK.stackGrade}`,
  };
}
function frontStyle (it) {
  // A twin (or any facing) catches the light a touch differently, so a pair never reads as copy-paste.
  const light = it.light * (1 + (hash01(it.id + ':lux') - 0.5) * 0.07);
  return { filter: `${isLifted(it) ? LOOK.liftShadow : LOOK.frontShadow} brightness(${light.toFixed(3)}) ${LOOK.frontGrade}` };
}
function clipStyle (it) {
  const b = box(it);
  return { top: `${BOARDS[it.board].front * PH.value + 3 - b.top}px`, width: `${Math.max(b.w * 1.12, 62)}px`, boxShadow: LOOK.clipShadow };
}

// --- lazy loading: the bay in view ± 1 ----------------------------------------

const loaded = ref(new Set());
function visibleBays () {
  const L = layout.value; const pw = PW.value || 1;
  const a = scrollX.value / pw; const z = (scrollX.value + vw.value) / pw;
  const idx = L.bays.map((b, i) => ((b.to > a && b.from < z) ? i : -1)).filter((i) => i >= 0);
  if (!idx.length) return [];
  const lo = Math.max(0, idx[0] - 1); const hi = Math.min(L.bays.length - 1, idx.at(-1) + 1);
  return L.bays.slice(lo, hi + 1).map((b) => b.key);
}
function markLoaded () {
  const keys = new Set(visibleBays());
  const add = layout.value.items.filter((it) => keys.has(it.bay) && !loaded.value.has(it.slug));
  if (add.length) loaded.value = new Set([...loaded.value, ...add.map((it) => it.slug)]);
}
const isLoaded = (it) => loaded.value.has(it.slug);

// --- which bay is in view ------------------------------------------------------

const bayInView = ref('');
function computeBayInView () {
  const L = layout.value; const pw = PW.value; if (!pw || !L.bays.length) return;
  const a = scrollX.value; const z = a + vw.value; const mid = (a + z) / 2;
  let best = null; let bestScore = -1;
  for (const b of L.bays) {
    const x0 = b.from * pw; const x1 = b.to * pw;
    const vis = Math.max(0, Math.min(x1, z) - Math.max(x0, a));
    if (!vis) continue;
    const frac = vis / Math.min(x1 - x0, vw.value);
    const score = Math.round(frac * 50) - Math.abs((x0 + x1) / 2 - mid) / (vw.value * 4);
    if (score > bestScore) { bestScore = score; best = b.key; }
  }
  if (a < 4 && L.bays[0]) best = L.bays[0].key;           // the start of the wall is its first bay
  if (best && best !== bayInView.value) { bayInView.value = best; emit('bayInView', best); }
}
const tabbable = (it) => it.bay === bayInView.value && !it.twin && !isDim(it);

// --- which bays are controls ---------------------------------------------------------
//
// Only the bags of a bay that is actually on screen are buttons. A bay is live
// when at least 60% of it is visible — of its own width, or of the viewport's
// when the bay is wider than the screen (a two-plate bay on a phone can never
// show more than about half of itself). Recomputed from the scroll position on
// every scroll frame (onScroll) and on resize (measure), which is the same
// signal an IntersectionObserver on each bay would give, without adding a
// sentinel element per bay. A bay being walked to by keyboard is live from the
// moment the walk starts, so focus can land in it before the pan finishes.
const LIVE_MIN = 0.6;
const liveBays = ref(new Set());
const pinned = new Set();
let pinTimer = 0;
function geomLive (b) {
  const pw = PW.value; const w = vw.value;
  if (!pw || !w) return false;
  const a = scrollX.value; const x0 = b.from * pw; const x1 = b.to * pw;
  const vis = Math.max(0, Math.min(x1, a + w) - Math.max(x0, a));
  return vis / Math.min(x1 - x0, w) >= LIVE_MIN - 0.001;
}
function computeLive () {
  const next = new Set();
  for (const b of layout.value.bays) {
    if (geomLive(b)) { next.add(b.key); pinned.delete(b.key); }   // arrived: the pin has done its job
  }
  for (const k of pinned) next.add(k);
  const cur = liveBays.value;
  if (next.size !== cur.size || [...next].some((k) => !cur.has(k))) liveBays.value = next;
}
function pinBay (key) {
  pinned.add(key);
  computeLive();
  clearTimeout(pinTimer);
  pinTimer = setTimeout(() => { pinned.clear(); computeLive(); }, 1500);
}
const isLive = (it) => props.interactive && props.active && liveBays.value.has(it.bay);

// --- scrolling ---------------------------------------------------------------------

let raf = 0;
let programmatic = 0;
let heading = '';          // the bay a programmatic pan is on its way to
let userMoved = false;
function onScroll () {
  if (raf) return;
  raf = requestAnimationFrame(() => {
    raf = 0;
    scrollX.value = host.value?.scrollLeft || 0;
    computeBayInView();
    computeLive();
    markLoaded();
    if (window.performance.now() > programmatic && !userMoved && interactiveUser) { userMoved = true; emit('drag'); }
  });
}
let interactiveUser = false;
const markUser = () => { interactiveUser = true; };

function targetFor (key) {
  const b = layout.value.bays.find((x) => x.key === key);
  if (!b) return null;
  const pw = PW.value; const bw = (b.to - b.from) * pw;
  let left;
  if (bw <= vw.value) left = b.from * pw - (vw.value - bw) / 2;
  else if (pw >= vw.value * 0.8) left = b.from * pw - (vw.value - pw) / 2;
  else left = b.from * pw - 12;
  return Math.max(0, Math.min(wallW.value - vw.value, left));
}

function scrollToX (left, instant) {
  const el = host.value; if (!el) return;
  programmatic = window.performance.now() + (instant ? 80 : 900);
  el.scrollTo({ left, behavior: instant || props.reducedMotion ? 'auto' : 'smooth' });
  if (instant) { scrollX.value = left; computeBayInView(); computeLive(); markLoaded(); }
}

function focusBay (key, { instant = false } = {}) {
  if (!PH.value || !vw.value) { pendingBay = key; return; }
  const left = targetFor(key);
  if (left == null) return;
  const add = layout.value.items.filter((it) => it.bay === key).map((it) => it.slug);
  if (add.length) loaded.value = new Set([...loaded.value, ...add]);
  if (!instant) pinBay(key);
  heading = key;
  scrollToX(left, instant);
}

function walk (dir) {
  const L = layout.value.bays;
  const i = L.findIndex((b) => b.key === bayInView.value);
  const next = L[Math.max(0, Math.min(L.length - 1, i + dir))];
  if (next) focusBay(next.key);
  return next?.key;
}

// Mouse drag to pan (touch and trackpads scroll natively, with their own momentum).
let drag = null;
let suppressClick = false;
function onDown (e) {
  if (e.pointerType !== 'mouse' || e.button !== 0 || !props.interactive) return;
  markUser();
  drag = { x: e.clientX, left: host.value.scrollLeft, t: window.performance.now(), v: 0, moved: false };
  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onUp, { once: true });
}
function onMove (e) {
  if (!drag) return;
  const dx = e.clientX - drag.x;
  if (!drag.moved && Math.abs(dx) < 5) return;
  drag.moved = true; dragging.value = true;
  const now = window.performance.now();
  const left = drag.left - dx;
  drag.v = (host.value.scrollLeft - left) / Math.max(1, now - drag.t) * -1;
  drag.t = now;
  host.value.scrollLeft = left;
}
function onUp () {
  window.removeEventListener('pointermove', onMove);
  if (!drag) return;
  const { moved, v } = drag; drag = null;
  if (!moved) return;
  suppressClick = true;
  setTimeout(() => { suppressClick = false; }, 0);
  // A short glide, then let the snap points settle it.
  let vel = v * 16; const el = host.value;
  const glide = () => {
    if (Math.abs(vel) < 0.5 || props.reducedMotion) { dragging.value = false; return; }
    el.scrollLeft += vel; vel *= 0.92; requestAnimationFrame(glide);
  };
  requestAnimationFrame(glide);
}
function onClickCapture (e) { if (suppressClick) { e.stopPropagation(); e.preventDefault(); } }

// Snapping waits until the wall has a size. The page can mount before Ionic has
// shown it, so every plate starts at left 0 and every snap position coincides;
// WebKit then keeps whichever of those it picked as "the snapped plate" and
// re-snaps to it once the plates spread out, which opened the wall at a random
// bay (measured: 513, 2567, 6675px on four loads of the same page).
const sized = ref(false);
const snapType = computed(() => (dragging.value || !props.interactive || !sized.value ? 'none' : 'x proximity'));

// --- picking -------------------------------------------------------------------------

function onPick (it, e) {
  if (!isLive(it) || isDim(it)) return;
  const el = e.currentTarget;
  const r = el.getBoundingClientRect();
  emit('pick', { slug: it.slug, rect: { x: r.left, y: r.top, width: r.width, height: r.height } });
}

function onKey (e) {
  if (!props.interactive) return;
  // Enter / Space on a bag: what a <button> would do for free. Handled here on
  // the scroller rather than on each bag, one listener for the whole wall.
  if (e.key === 'Enter' || e.key === ' ') {
    const node = e.target?.closest?.('.slot[role="button"]');
    const it = node && layout.value.items.find((x) => slots.get(x.id) === node);
    if (it) { e.preventDefault(); onPick(it, { currentTarget: node }); }
    return;
  }
  if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
    markUser();
    const key = walk(e.key === 'ArrowRight' ? 1 : -1);
    e.preventDefault();
    if (key && host.value?.contains(document.activeElement)) {
      nextTick(() => {
        const first = layout.value.items
          .filter((it) => it.bay === key && !it.twin && !isDim(it))
          .sort((a, b) => a.board - b.board || a.plate + a.x - (b.plate + b.x))[0];
        slots.get(first?.id)?.focus({ preventScroll: true });
      });
    }
  } else if (e.key === 'Escape' && props.picked) {
    emit('release'); e.preventDefault();
  }
}

// --- the re-rack (FLIP) ------------------------------------------------------------

const slots = new Map();
function setSlot (id, el) { const node = el?.$el || el; if (node) slots.set(id, node); else slots.delete(id); }

let rerackTimer = 0;
async function rerack () {
  const el = host.value;
  const next = layoutWall(wallInput(props.bays));
  if (!el || !PH.value) { layout.value = next; return; }
  const hostBox = el.getBoundingClientRect();
  // Where every bag is on screen right now (mid-flight ones included).
  const before = new Map();
  let anchor = null; let anchorD = Infinity;
  const mid = hostBox.left + hostBox.width / 2;
  for (const [id, node] of slots) {
    const r = node.getBoundingClientRect();
    before.set(id, { x: r.left - hostBox.left, y: r.top - hostBox.top, w: r.width });
    const d = Math.abs(r.left + r.width / 2 - mid);
    if (r.width && d < anchorD && !id.endsWith(':twin')) { anchorD = d; anchor = { id, x: r.left - hostBox.left + r.width / 2 }; }
  }
  layout.value = next;
  await nextTick();
  // Keep the coffee that was in the middle of the view where it was: the wall
  // re-racks around it.
  const a = anchor && next.items.find((it) => it.id === anchor.id);
  let left = 0;
  if (a) { const b = box(a); left = b.left + b.w / 2 - anchor.x; }
  left = Math.max(0, Math.min(wallW.value - vw.value, left));
  el.scrollLeft = left; scrollX.value = left;
  programmatic = window.performance.now() + 200;
  computeBayInView(); computeLive(); markLoaded();

  clearTimeout(rerackTimer);
  if (props.reducedMotion) {
    fading.value = true;
    requestAnimationFrame(() => requestAnimationFrame(() => { fading.value = false; }));
    return;
  }
  const { ms, stagger, maxStagger } = LOOK.rerack;
  const moving = [];
  for (const it of next.items) {
    const node = slots.get(it.id); if (!node) continue;
    const b = box(it); const nx = b.left - left;
    if (nx + b.w < -vw.value * 0.25 || nx > vw.value * 1.25) continue;   // off screen: just be there
    const old = before.get(it.id);
    node.style.transition = 'none';
    if (old) {
      let dx = old.x - nx; const dy = old.y - (b.top + offsetY.value);
      const far = Math.abs(dx) > vw.value * 1.2;
      if (far) dx = Math.sign(dx) * vw.value * 0.7;
      node.style.transform = `translate(${dx}px, ${dy}px) scale(${old.w / b.w || 1})`;
      if (far) node.style.opacity = '0';
    } else {
      node.style.opacity = '0';
      node.style.transform = `translateY(${-12}px)`;
    }
    moving.push({ node, order: it.plate + it.x + it.board * 0.01 });
  }
  if (!moving.length) return;
  moving.sort((p, q) => p.order - q.order);
  void el.offsetWidth; // commit the inverted positions
  moving.forEach(({ node }, i) => {
    const delay = Math.min(i, maxStagger) * stagger;
    node.style.transition = `transform ${ms}ms cubic-bezier(.2, .75, .2, 1) ${delay}ms, opacity ${ms * 0.3}ms ease-out ${delay}ms`;
    node.style.transform = '';
    node.style.opacity = '';
  });
  rerackTimer = setTimeout(() => { for (const { node } of moving) node.style.transition = ''; }, ms + maxStagger * stagger + 60);
}

watch(signature, () => { rerack(); });

// --- readiness ------------------------------------------------------------------------

const readyIds = new Set();
let isReady = false;
let platesDecoded = false;
function checkReady () {
  if (isReady || !platesDecoded) return;
  // Only the bay you are looking at has to be on the shelves; its neighbours load behind it.
  const here = layout.value.items.filter((it) => loaded.value.has(it.slug) && !it.twin);
  const inView = here.filter((it) => it.bay === bayInView.value);
  const want = inView.length ? inView : here;
  const seenN = want.filter((it) => readyIds.has(it.id)).length;
  if (!want.length || seenN >= Math.ceil(want.length * 0.8)) { isReady = true; emit('ready'); }
}
function onFrontReady (it) { readyIds.add(it.id); checkReady(); }

// --- lifecycle ---------------------------------------------------------------------------

let ro = null;
let pendingBay = props.initialBay;
// A new scale (resize, rotation, the band changing) keeps the same bay in view.
watch(PH, (ph, old) => {
  // Mid-pan to a bay, keep going to that bay, not whichever one is passing by.
  const key = (window.performance.now() < programmatic && heading) || bayInView.value;
  if (key && old && ph && old !== ph) nextTick(() => focusBay(key, { instant: true }));
});
watch(() => props.initialBay, (k) => { if (k) focusBay(k, { instant: true }); });
function measure () {
  const el = host.value; if (!el) return;
  vw.value = el.clientWidth; vh.value = el.clientHeight;
  scrollX.value = el.scrollLeft;
  if (!sized.value && vw.value && PH.value) requestAnimationFrame(() => { sized.value = true; });
  // A page can mount before it has a size (Ionic shows it after the transition).
  if (pendingBay && vw.value && PH.value) { const k = pendingBay; pendingBay = ''; nextTick(() => focusBay(k, { instant: true })); }
  computeBayInView(); computeLive(); markLoaded();
}
const onWheelOrTouch = () => markUser();

onMounted(() => {
  measure();
  ro = new ResizeObserver(() => { measure(); });
  ro.observe(host.value);
  host.value.addEventListener('wheel', onWheelOrTouch, { passive: true });
  host.value.addEventListener('touchstart', onWheelOrTouch, { passive: true });
  Promise.all(PLATE_SRC.map((src) => { const i = new window.Image(); i.src = src; return i.decode().catch(() => {}); }))
    .then(() => { platesDecoded = true; checkReady(); });
  // Never hold the wall behind a slow image.
  setTimeout(() => { if (!isReady) { isReady = true; emit('ready'); } }, 2500);
});
onBeforeUnmount(() => {
  ro?.disconnect();
  cancelAnimationFrame(raf);
  clearTimeout(rerackTimer);
  clearTimeout(pinTimer);
  window.removeEventListener('pointermove', onMove);
  host.value?.removeEventListener('wheel', onWheelOrTouch);
  host.value?.removeEventListener('touchstart', onWheelOrTouch);
});

defineExpose({
  focusBay,
  walk,
  bayItems: (key) => layout.value.items.filter((it) => it.bay === key),
  itemOf: (slug) => bySlug.value.get(slug) || null,
  get currentBayKey () { return bayInView.value; },
  get layout () { return layout.value; },
});
</script>

<style scoped>
.wall-scroller {
  position: absolute;
  inset: 0;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  overscroll-behavior-x: contain;
  -webkit-overflow-scrolling: touch;
  background: var(--paper, #F4EFE6);
  cursor: grab;
  outline: none;
}
.wall-scroller::-webkit-scrollbar { display: none; }
.wall-scroller.is-dragging { cursor: grabbing; }
.wall-scroller.is-dragging .slot { pointer-events: none; }
.wall-scroller.is-static { overflow: hidden; cursor: default; pointer-events: none; }

.wall { position: relative; transition: opacity 180ms linear; }
.wall--fade { opacity: 0; transition: none; }

.plate {
  position: absolute;
  top: 0;
  height: 100%;
  background-size: 100% 100%;
  background-repeat: no-repeat;
}
/* The top-edge fill (see frame): the plate's top rows, mirrored upward. */
.plate::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(100% - 1px);            /* overlap the seam by a pixel, and skip */
  height: var(--fill, 0px);            /* the photo's outermost rows, so no hairline shows */
  background-image: inherit;
  background-size: 100% var(--ph, 100%);
  background-position: 50% -3px;
  background-repeat: no-repeat;
  transform: scaleY(-1);
}

.slot {
  position: absolute;
  display: block;
  padding: 0;
  margin: 0;
  border: 0;
  background: none;
  font: inherit;
  color: inherit;
  cursor: pointer;
  transform-origin: 0 0;
  -webkit-tap-highlight-color: transparent;
}
.slot--dim, .slot--off { cursor: default; }
.slot:focus-visible { outline: none; }
.slot:focus-visible .bag::after {
  content: '';
  position: absolute;
  inset: -4px;
  border-radius: 6px;
  box-shadow: 0 0 0 2px var(--dock, #FAF7F1), 0 0 0 4px var(--ink, #1F2A22);
}

.bag {
  position: absolute;
  inset: 0;
  transform-origin: 50% 100%;
  transition-property: transform, filter;
  transition-timing-function: cubic-bezier(.2, .8, .2, 1), ease-out;
}
.slot--lifted { z-index: 2; }

.bag .layer { position: absolute; inset: 0; width: 100%; height: 100%; transform-origin: 50% 100%; }
.bag .layer--front { transition: filter 180ms ease-out; }

.contact {
  position: absolute;
  left: 2%;
  right: 2%;
  bottom: -3px;
  height: 9px;
  border-radius: 50%;
  filter: blur(1.5px);
  transition: opacity 180ms ease-out;
}
.slot--lifted .contact { opacity: 0.35; }

.clip {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  color: var(--ink, #1F2A22);
  background: var(--dock, #FAF7F1);
  padding: 3px 3px 3px;
  border-radius: 1.5px;
  text-align: center;
  pointer-events: none;
}
.clip > span { max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; line-height: 1.1; }
.clip > .clip__name { white-space: normal; display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 3; line-clamp: 3; }
.clip__brand {
  font-family: var(--font-body, 'Manrope', system-ui, sans-serif);
  font-weight: 700;
  font-size: calc(var(--text-xs, 12px) * 0.5);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted, #5E6B60);
}
.clip__name {
  font-family: var(--font-body, 'Manrope', system-ui, sans-serif);
  font-weight: 800;
  font-size: calc(var(--text-xs, 12px) * 0.6);
  letter-spacing: -0.015em;
}
.clip__price {
  font-family: var(--font-mono, 'Martian Mono', ui-monospace, monospace);
  font-weight: 500;
  font-size: calc(var(--text-xs, 12px) * 0.66);
  margin-top: 1px;
}
.clip--out .clip__price { color: var(--muted, #5E6B60); }

@media (min-width: 900px) {
  .clip__name { font-size: calc(var(--text-xs, 12px) * 0.68); }
  .clip__price { font-size: calc(var(--text-xs, 12px) * 0.75); }
  .clip__brand { font-size: calc(var(--text-xs, 12px) * 0.58); }
}
@media (prefers-reduced-motion: reduce) {
  .bag, .layer--front, .contact, .clip, .wall { transition: none; }
}
</style>
